import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  useCreate,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate,
} from 'react-admin';

const MANAGER_PAIRS = [
  {
    roleKey: 'AREA',
    incumbent: { source: 'area_manager', labelKey: 'area_manager' },
    substitute: {
      source: 'backup_area_manager',
      labelKey: 'backup_area_manager',
    },
  },
  {
    roleKey: 'ACCOUNT',
    incumbent: { source: 'account_manager', labelKey: 'account_manager' },
    substitute: {
      source: 'backup_account_manager',
      labelKey: 'backup_account_manager',
    },
  },
  {
    roleKey: 'QUALITY',
    incumbent: { source: 'quality_manager', labelKey: 'quality_manager' },
    substitute: {
      source: 'backup_quality_manager',
      labelKey: 'backup_quality_manager',
    },
  },
];

const MANAGER_SLOTS = MANAGER_PAIRS.flatMap((pair) => {
  return [pair.incumbent, pair.substitute];
});

const CLEANER_FILTER = {
  'in(function)': ['Admin', 'Manager'],
  employee_status: 'Salarié actuel',
};
const CLEANER_PAGINATION = { page: 1, perPage: 200 };
const CLEANER_SORT = { field: 'fullname', order: 'ASC' };

const INFO_PAGINATION = { page: 1, perPage: 1 };
const INFO_SORT = { field: 'created_at', order: 'DESC' };

const RESOURCE = 'account_operational_info';

const useStyles = makeStyles((theme) => {
  return {
    container: {
      padding: theme.spacing(2),
    },
    fieldsRow: {
      display: 'flex',
      justifyContent: 'space-around',
      flexWrap: 'wrap',
      gap: theme.spacing(3),
    },
    fieldWrapper: {
      flex: '1 1 280px',
      maxWidth: 360,
    },
    section: {
      marginBottom: theme.spacing(4),
    },
    sectionTitle: {
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
      textAlign: 'center',
    },
    sectionDivider: {
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.divider,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    loader: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    error: {
      color: theme.palette.error.main,
      padding: theme.spacing(2),
    },
  };
});

const buildInitialValues = (record) => {
  return MANAGER_SLOTS.reduce((acc, { source }) => {
    acc[source] = record && record[source] ? record[source] : null;
    return acc;
  }, {});
};

const SiteManagementForm = () => {
  const classes = useStyles();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const account = useRecordContext();
  const accountId = account ? account.id : null;

  const {
    data: infoData,
    ids: infoIds,
    loading: infoLoading,
    error: infoError,
  } = useGetList(
    RESOURCE,
    INFO_PAGINATION,
    INFO_SORT,
    { account_id: accountId },
    { enabled: Boolean(accountId) },
  );

  const currentInfo = useMemo(() => {
    if (!infoIds || infoIds.length === 0) {
      return null;
    }
    return infoData[infoIds[0]] || null;
  }, [infoData, infoIds]);

  const {
    data: cleanerData,
    ids: cleanerIds,
    loading: cleanersLoading,
    error: cleanersError,
  } = useGetList('cleaner', CLEANER_PAGINATION, CLEANER_SORT, CLEANER_FILTER);

  const cleanerOptions = useMemo(() => {
    if (!cleanerIds) {
      return [];
    }
    return cleanerIds
      .map((id) => {
        return cleanerData[id];
      })
      .filter(Boolean);
  }, [cleanerData, cleanerIds]);

  const [values, setValues] = useState(buildInitialValues(null));

  useEffect(() => {
    setValues(buildInitialValues(currentInfo));
  }, [currentInfo]);

  const [update, { loading: updating }] = useUpdate();
  const [create, { loading: creating }] = useCreate();

  const saving = updating || creating;

  const handleChange = (source) => {
    return (_event, cleaner) => {
      setValues((previous) => {
        return { ...previous, [source]: cleaner ? cleaner.id : null };
      });
    };
  };

  const handleReset = () => {
    setValues(buildInitialValues(currentInfo));
  };

  const handleSave = () => {
    if (!accountId) {
      return;
    }

    const payload = { ...values, account_id: accountId };

    const onSuccess = () => {
      notify('resources.account.notifications.site_management_saved', 'info');
      refresh();
    };

    const onFailure = (error) => {
      notify(
        typeof error === 'string'
          ? error
          : error.message || 'ra.notification.http_error',
        'warning',
      );
    };

    if (currentInfo && currentInfo.id) {
      update(
        RESOURCE,
        currentInfo.id,
        { ...payload, id: currentInfo.id },
        currentInfo,
        { onSuccess, onFailure },
      );
      return;
    }

    create(RESOURCE, payload, { onSuccess, onFailure });
  };

  if (!accountId || infoLoading || cleanersLoading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  if (infoError || cleanersError) {
    const error = infoError || cleanersError;
    return (
      <Typography className={classes.error}>
        {translate('ra.notification.http_error')} : {error.message}
      </Typography>
    );
  }

  const getOptionByCleanerId = (id) => {
    if (!id) {
      return null;
    }
    return (
      cleanerOptions.find((cleaner) => {
        return cleaner.id === id;
      }) || null
    );
  };

  const renderManagerField = ({ source, labelKey }) => {
    return (
      <Autocomplete
        options={cleanerOptions}
        value={getOptionByCleanerId(values[source])}
        onChange={handleChange(source)}
        getOptionLabel={(cleaner) => {
          return cleaner && cleaner.fullname ? cleaner.fullname : '';
        }}
        getOptionSelected={(option, selected) => {
          return option.id === selected.id;
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={translate(
                `resources.account_operational_info.fields.${labelKey}`,
              )}
              variant="filled"
              fullWidth
            />
          );
        }}
      />
    );
  };

  return (
    <div className={classes.container}>
      {MANAGER_PAIRS.map(({ roleKey, incumbent, substitute }) => {
        return (
          <div className={classes.section} key={roleKey}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {translate(
                `resources.account_operational_info.role.${roleKey}`,
              )}
            </Typography>
            <Divider className={classes.sectionDivider} />
            <div className={classes.fieldsRow}>
              <div className={classes.fieldWrapper}>
                {renderManagerField(incumbent)}
              </div>
              <div className={classes.fieldWrapper}>
                {renderManagerField(substitute)}
              </div>
            </div>
          </div>
        );
      })}

      <div className={classes.actions}>
        <Button onClick={handleReset} disabled={saving}>
          {translate('ra.action.refresh')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            translate('ra.action.save')
          )}
        </Button>
      </div>
    </div>
  );
};

SiteManagementForm.propTypes = {
  record: PropTypes.object,
};

SiteManagementForm.defaultProps = {
  record: undefined,
};

export default SiteManagementForm;
